import React from 'react';
import { Users, Clock, CheckCircle, XCircle, TrendingUp, AlertCircle } from 'lucide-react';
import { WaitlistStatsResponse } from '../types';

interface WaitlistStatsCardProps {
  stats: WaitlistStatsResponse;
}

/**
 * WaitlistStatsCard - Display waitlist statistics for fair managers
 * Shows key metrics like conversion rate, wait times, and status counts
 */
function WaitlistStatsCard({ stats }: WaitlistStatsCardProps) {
  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const formatWaitTime = (days: number | null) => {
    if (days === null) return 'N/A';
    if (days < 1) return '< 1 day';
    if (days === 1) return '1 day';
    return `${Math.round(days)} days`;
  };

  const totalActive = stats.total_waiting + stats.total_notified;
  const totalProcessed = stats.total_converted + stats.total_expired + stats.total_cancelled;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Waitlist Statistics</h3>
        <div className="text-sm text-gray-600">{stats.fair_name}</div>
      </div>

      {/* Primary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Conversion Rate */}
        <div className="bg-gradient-to-br from-teal-50 to-teal-100 border border-teal-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-secondary" />
            <span className="text-sm font-medium text-teal-900">Conversion Rate</span>
          </div>
          <div className="text-3xl font-bold text-primary-dark">
            {formatPercentage(stats.conversion_rate)}
          </div>
        </div>

        {/* Active in Queue */}
        <div className="bg-gradient-to-br from-teal-50 to-moonbone border border-teal-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-secondary" />
            <span className="text-sm font-medium text-ink">Active in Queue</span>
          </div>
          <div className="text-3xl font-bold text-primary-dark">{totalActive}</div>
        </div>

        {/* Avg Wait Time */}
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-900">Avg Wait Time</span>
          </div>
          <div className="text-3xl font-bold text-gray-700">
            {formatWaitTime(stats.average_wait_time_days)}
          </div>
        </div>
      </div>

      {/* Status Breakdown */}
      <div className="border-t border-gray-200 pt-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Status Breakdown</h4>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {/* Waiting */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Clock className="w-4 h-4 text-secondary" />
              <span className="text-xs text-gray-600">Waiting</span>
            </div>
            <div className="text-2xl font-bold text-primary-dark">{stats.total_waiting}</div>
          </div>

          {/* Notified */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <AlertCircle className="w-4 h-4 text-secondary" />
              <span className="text-xs text-gray-600">Notified</span>
            </div>
            <div className="text-2xl font-bold text-primary-dark">{stats.total_notified}</div>
          </div>

          {/* Converted */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-xs text-gray-600">Converted</span>
            </div>
            <div className="text-2xl font-bold text-green-700">{stats.total_converted}</div>
          </div>

          {/* Expired */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <XCircle className="w-4 h-4 text-gray-600" />
              <span className="text-xs text-gray-600">Expired</span>
            </div>
            <div className="text-2xl font-bold text-gray-700">{stats.total_expired}</div>
          </div>

          {/* Cancelled */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <XCircle className="w-4 h-4 text-red-600" />
              <span className="text-xs text-gray-600">Cancelled</span>
            </div>
            <div className="text-2xl font-bold text-red-700">{stats.total_cancelled}</div>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="border-t border-gray-200 mt-4 pt-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">Total Processed:</span>
          <span className="font-semibold text-gray-900">{totalProcessed}</span>
        </div>
      </div>
    </div>
  );
}

export default WaitlistStatsCard;
